import type {
  ActiveContext,
  ActiveContextMode,
  Document as AppDocument,
  Message,
} from '../types';

const GROUNDING_TO_MODE: Record<string, ActiveContextMode> = {
  document_analysis: 'document',
  email_analysis: 'email',
  email_analysis_error: 'email',
  calendar_analysis: 'calendar',
  calendar_analysis_error: 'calendar',
  scheduling_analysis: 'scheduling',
  scheduling_analysis_error: 'scheduling',
  agent_bing_grounding: 'web_search',
  web_search_unavailable: 'web_search',
  general: 'general',
};

function compactText(value: string | undefined, maxLength = 180): string | undefined {
  if (!value) return undefined;
  const normalized = value.replace(/\s+/g, ' ').trim();
  if (!normalized) return undefined;
  return normalized.length > maxLength ? `${normalized.slice(0, maxLength - 1)}…` : normalized;
}

function extractOptionsFromText(content: string): string[] {
  const options: string[] = [];
  const seen = new Set<string>();
  const optionRegex = /option\s+(\d+)\s*[:.-]\s*([^\n]+)/gi;

  let match: RegExpExecArray | null;
  while ((match = optionRegex.exec(content)) !== null) {
    const label = compactText(match[2], 120);
    if (label && !seen.has(label.toLowerCase())) {
      seen.add(label.toLowerCase());
      options.push(label);
    }
  }

  return options;
}

function resolveMode(messages: Message[], documents: AppDocument[]): ActiveContextMode {
  if (documents.length > 0) return 'document';

  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const source = messages[i].groundingSource;
    if (source && GROUNDING_TO_MODE[source]) {
      return GROUNDING_TO_MODE[source];
    }
  }

  return 'general';
}

function resolveSelectedOptionHint(query: string, options: string[]): string | undefined {
  const match = query.match(/\boption\s+(\d+)\b/i);
  if (!match) return undefined;
  const index = parseInt(match[1], 10) - 1;
  if (index < 0 || index >= options.length) return undefined;
  return options[index];
}

export function buildActiveContext(
  messages: Message[],
  documents: AppDocument[],
  latestUserQuery?: string
): ActiveContext {
  const recentMessages = messages.slice(-8);
  const lastAssistant = [...recentMessages].reverse().find((message) => !message.isUser);
  const lastUser = [...recentMessages].reverse().find((message) => message.isUser);
  const optionsPresented = recentMessages
    .filter((message) => !message.isUser)
    .flatMap((message) => extractOptionsFromText(message.content))
    .slice(0, 6);

  const selectedOptionHint = latestUserQuery
    ? resolveSelectedOptionHint(latestUserQuery, optionsPresented)
    : undefined;

  return {
    mode: resolveMode(recentMessages, documents),
    lastTask: compactText(lastUser?.content),
    optionsPresented: optionsPresented.length > 0 ? optionsPresented : undefined,
    selectedOptionHint,
    documentNames: documents.map((document) => document.originalFileName || document.fileName).slice(0, 5),
    lastAssistantGroundingSource: lastAssistant?.groundingSource,
  };
}
